import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, GraduationCap, Search, Users } from 'lucide-react';
import PageHero from '../components/common/PageHero';
import api from '../services/api';
import { alumniPhotoUrl } from '../utils/assets';

/** Leadership roles get the gold treatment; officers a quieter navy badge. */
const LEADERSHIP = ['Chairman', 'Vice Chairman', 'Patron'];
const OFFICERS = ['Secretary', 'Treasurer', 'Organizing Secretary', 'Advisor', 'Auditor'];

const roleBadgeClass = (role) => {
  if (LEADERSHIP.includes(role)) return 'badge-gold';
  if (OFFICERS.includes(role)) return 'badge-navy';
  return 'badge-neutral';
};

const initials = (name) =>
  (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

const AlumniPage = () => {
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');

  useEffect(() => {
    let cancelled = false;

    const fetchAlumni = async () => {
      try {
        const response = await api.get('/alumni');
        if (!cancelled) setAlumni(response.data);
      } catch (error) {
        console.error('Error fetching alumni:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAlumni();
    return () => {
      cancelled = true;
    };
  }, []);

  const roles = ['All', ...new Set(alumni.map((member) => member.role).filter(Boolean))];

  const filteredAlumni = alumni.filter((member) => {
    const term = query.trim().toLowerCase();
    const matchesRole = roleFilter === 'All' || member.role === roleFilter;
    if (!matchesRole) return false;
    if (!term) return true;
    return [member.name, member.department_name, member.role, member.graduation_year]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(term));
  });

  return (
    <div className="bg-ink-50">
      <PageHero
        icon={Users}
        title="Katine TTI Alumni Association"
        subtitle="Meet our distinguished alumni who are making a difference"
      />

      <div className="container-page py-10 sm:py-12">
        {loading ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="card overflow-hidden p-0">
                <div className="h-56 animate-pulse bg-ink-200" />
                <div className="space-y-2 p-4">
                  <div className="h-4 w-2/3 animate-pulse rounded bg-ink-200" />
                  <div className="h-3 w-1/2 animate-pulse rounded bg-ink-100" />
                </div>
              </div>
            ))}
          </div>
        ) : alumni.length === 0 ? (
          <div className="card mx-auto max-w-md text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-50 text-brand-600">
              <Users className="h-7 w-7" aria-hidden="true" />
            </span>
            <h2 className="mt-4 text-lg font-bold text-ink-900">No alumni listed yet</h2>
            <p className="mt-1.5 text-sm text-ink-500">
              Committee members will appear here once they have been added.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
              <h2 className="text-lg font-bold text-ink-900">
                Committee members
                <span className="ml-2 rounded-full bg-brand-100 px-2 py-0.5 align-middle text-xs font-semibold text-brand-700">
                  {filteredAlumni.length}
                </span>
              </h2>
              <Link
                to="/alumni/register"
                className="text-sm font-semibold text-brand-700 hover:underline"
              >
                Join the alumni association
              </Link>
            </div>

            {/* Search and role filter */}
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="relative w-full sm:max-w-xs">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400"
                  aria-hidden="true"
                />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name or department"
                  aria-label="Search alumni"
                  className="input-field py-2 pl-9 text-sm"
                />
              </div>

              <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by role">
                {roles.map((role) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => setRoleFilter(role)}
                    aria-pressed={roleFilter === role}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                      roleFilter === role
                        ? 'bg-brand-700 text-white'
                        : 'bg-white text-ink-600 ring-1 ring-ink-200 hover:bg-ink-100'
                    }`}
                  >
                    {role}
                  </button>
                ))}
              </div>
            </div>

            {filteredAlumni.length === 0 ? (
              <div className="card mx-auto max-w-md text-center">
                <p className="text-sm font-medium text-ink-700">No members match your filters</p>
                <button
                  type="button"
                  onClick={() => {
                    setQuery('');
                    setRoleFilter('All');
                  }}
                  className="btn-ghost mx-auto mt-2 text-sm"
                >
                  Clear filters
                </button>
              </div>
            ) : (
            <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filteredAlumni.map((alumnus) => {
                const photo = alumniPhotoUrl(alumnus.photo_url);

                return (
                  <li key={alumnus.id}>
                    <Link
                      to={`/alumni/${alumnus.id}`}
                      className="group flex h-full flex-col overflow-hidden rounded-xl border border-ink-200 bg-white shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-brand-200 hover:shadow-card-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                    >
                      <div className="relative aspect-[4/3] overflow-hidden bg-brand-800">
                        <span
                          className="absolute inset-0 flex items-center justify-center text-3xl font-bold text-white/70"
                          aria-hidden="true"
                        >
                          {initials(alumnus.name)}
                        </span>
                        {photo ? (
                          <img
                            src={photo}
                            alt={`Portrait of ${alumnus.name}`}
                            loading="lazy"
                            className="relative h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : null}
                      </div>

                      <div className="flex flex-1 flex-col p-4">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="min-w-0 truncate font-semibold text-ink-900">
                            {alumnus.name}
                          </h3>
                          <span className={`${roleBadgeClass(alumnus.role)} flex-shrink-0`}>
                            {alumnus.role}
                          </span>
                        </div>

                        {alumnus.department_name && (
                          <p className="mt-1 truncate text-sm text-ink-500">
                            {alumnus.department_name}
                          </p>
                        )}

                        {alumnus.graduation_year && (
                          <p className="mt-1 inline-flex items-center gap-1 text-xs text-ink-400">
                            <GraduationCap className="h-3.5 w-3.5" aria-hidden="true" />
                            Class of {alumnus.graduation_year}
                          </p>
                        )}

                        <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-700">
                          View profile
                          <ArrowRight
                            className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
                            aria-hidden="true"
                          />
                        </span>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AlumniPage;
